import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce, isEmpty } from 'lodash';
import {
  useGetAllProductsCalculationsRevenue,
  useGetCalculationsOnProductUpdateRevenue,
} from 'services/calculations/revenuePlan/hooks';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { initialPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';

import ToastService from 'helpers/ToastService';

import {
  getProductFieldsForRevenuePlan,
  isAllRevenuePlanRequiredFieldsFilled,
} from '../../helpers/revenuePlan/revenuePlanCalculation';
import { getUpdatedProductData, productChangeType } from '../../helpers/shared/shared';
import { IFormContentTypes, observedFields, PRODUCT_CHANGE_TYPE } from '../../utils/form';
import { useOrderTableValues } from '../useOrderTableValues';

export const useRevenuePlanCalculation = () => {
  const { id: businessPlanId = '' } = useParams();

  const { setValue } = useFormContext();

  const getUpdatedProductCalculation = useGetCalculationsOnProductUpdateRevenue();
  const getAllProductsCalculations = useGetAllProductsCalculationsRevenue();

  const orderTableValues = useOrderTableValues();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.FINANCIAL_PLAN,
    FOR_BANK_SUB_SECTIONS[FOR_BANK_SECTIONS.FINANCIAL_PLAN].REVENUE_PLAN,
  );

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (currentProductsArray, previousProductsArray, tableName, totalFieldName, unit, otherIncome) => {
        const updatedProduct = getUpdatedProductData(currentProductsArray, previousProductsArray);

        if (updatedProduct) {
          if (!isAllRevenuePlanRequiredFieldsFilled(updatedProduct)) return;

          const body = {
            unit,
            id: updatedProduct.productId || '',
            businessPlanId,
            product: getProductFieldsForRevenuePlan(updatedProduct),
            otherIncome,
          };
          const updatedCalculation = await getUpdatedProductCalculation.mutateAsync(body);

          setValue(tableName, orderTableValues(currentProductsArray, updatedCalculation.revenueData));
          setValue(totalFieldName, updatedCalculation.totalRevenueData);
        } else {
          // currency / otherIncome update calculation
          const calculationAfterDelete = await getAllProductsCalculations.mutateAsync({
            businessPlanId,
            unit,
            otherIncome,
          });
          if (isEmpty(calculationAfterDelete.revenueData)) {
            setValue(totalFieldName, initialPeriodsTableRow);
          } else {
            setValue(tableName, orderTableValues(currentProductsArray, calculationAfterDelete.revenueData));
            setValue(totalFieldName, calculationAfterDelete.totalRevenueData);
          }
        }
      }, 300),
     
    [],
  );

  const revenuePlanCalculationHandler = useCallback(
    async (content: IFormContentTypes['revenuePlan'], previousContent: IFormContentTypes['revenuePlan']) => {
      if (!canAccess) return;

      const [tableName, totalFieldName] = observedFields.revenuePlan.setCalculatedValueFieldNames;
      const currentProductsArray = content[0];
      const otherIncome = content[1][0];
      const unit = content[2] || DIMENSIONALITY_VALUES.SINGLE;
      const previousProductsArray = previousContent[0];

      const changeType = productChangeType(currentProductsArray, previousProductsArray);

      try {
        if (changeType === PRODUCT_CHANGE_TYPE.REMOVE) {
          const calculationAfterDelete = await getAllProductsCalculations.mutateAsync({
            businessPlanId,
            unit,
            otherIncome,
          });
          if (isEmpty(calculationAfterDelete.revenueData)) {
            setValue(totalFieldName, initialPeriodsTableRow);
          } else {
            setValue(tableName, orderTableValues(currentProductsArray, calculationAfterDelete.revenueData));
            setValue(totalFieldName, calculationAfterDelete.totalRevenueData);
          }
        } else if (changeType === PRODUCT_CHANGE_TYPE.UPDATE) {
          debouncedUpdateFunction(
            currentProductsArray,
            previousProductsArray,
            tableName,
            totalFieldName,
            unit,
            otherIncome,
          );
        }
      } catch (error) {
        if (error instanceof Error) ToastService.showError(error.message);
      }
    },
     
    [debouncedUpdateFunction],
  );

  return revenuePlanCalculationHandler;
};
